import type { ReactNode } from "react";
import { useThemeStyles } from "../hooks/useThemeStyles";
import { useFontLoader } from "../hooks/useFontLoader";
import { usePortalTheming } from "../store/portalSchemaStore";

interface OrganizationThemeProviderProps {
  children: ReactNode;
}

/**
 * Provider component that applies organization theme colors to its children.
 *
 * This component reads theme colors from the portal schema and applies them
 * as CSS custom properties via inline styles. The `display: contents` style
 * ensures the wrapper div doesn't affect the layout of its children.
 *
 * Use this component to wrap content that should be themed according to the
 * organization's brand colors (primary and secondary colors).
 *
 * @example
 * ```tsx
 * // In EnduserRouteHandler or similar top-level enduser component
 * export const EnduserRouteHandler: React.FC = () => {
 *   return (
 *     <OrganizationThemeProvider>
 *       <YourContent />
 *     </OrganizationThemeProvider>
 *   );
 * };
 * ```
 */
export const OrganizationThemeProvider: React.FC<OrganizationThemeProviderProps> = ({ children }) => {
  const theming = usePortalTheming();
  const themeStyles = useThemeStyles(theming);

  // Load custom font for the portal
  useFontLoader(theming?.fontFamily, theming?.customFontUrl);

  return (
    <div style={themeStyles} className="contents">
      {children}
    </div>
  );
};
